import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { sanitizeCode, slugify } from '../../lib/codeUtils';
import { dictionariesApi, type DictionaryFieldDataType, type DictionaryFieldDetail } from '../../lib/dictionariesApi';

type FieldState = DictionaryFieldDetail & { codeTouched?: boolean };

const dataTypeLabels: Record<DictionaryFieldDataType, string> = {
  string: 'Строка',
  text: 'Текст',
  integer: 'Целое',
  decimal: 'Дробное',
  boolean: 'Логич.',
  date: 'Дата',
  datetime: 'Дата+время',
};

export function AddDictionaryPage() {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [fields, setFields] = useState<FieldState[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const addField = () => {
    setFields((prev) => [
      ...prev,
      { code: `field_${prev.length + 1}`, name: '', data_type: 'string', required: false, codeTouched: false },
    ]);
  };
  const removeField = (index: number) => setFields((prev) => prev.filter((_, i) => i !== index));

  const handleCreate = async () => {
    setCreateError(null);
    setCreating(true);
    try {
      const created = await dictionariesApi.createDictionary({
        name: name.trim(),
        code: code.trim(),
        fields: fields.map(({ code: c, name: n, data_type, required }) => ({ code: c, name: n, data_type, required })),
      });
      navigate(`/app/dictionaries/${created.id}`);
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Не удалось создать справочник.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex h-screen w-full flex-col overflow-y-auto bg-bg-canvas">
      <header className="flex h-[72px] shrink-0 items-center gap-4 border-b border-border-default bg-bg-surface px-8">
        <Link to="/app/dictionaries" className="text-sm font-medium text-text-secondary hover:text-text-primary">
          ← Справочники
        </Link>
        <h1 className="text-[17px] font-semibold text-text-primary">Новый справочник</h1>
        <div className="ml-auto">
          <Link
            to="/app/dictionaries"
            className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
          >
            Отмена
          </Link>
        </div>
      </header>

      <div className="flex-1 px-8 py-8">
        <div className="mx-auto max-w-[900px] rounded-2xl border border-border-default bg-bg-surface p-6">
          <h2 className="text-base font-semibold text-text-primary">Название</h2>
          <label className="mt-4 text-[11px] font-medium text-text-secondary">Название справочника</label>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!code.trim()) setCode(slugify(e.target.value, 'dictionary'));
            }}
            placeholder="Например, Справочник компаний"
            className="mt-1.5 h-10 w-full max-w-[420px] rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
          />
          <label className="mt-3 block text-[11px] font-medium text-text-secondary">Код</label>
          <input
            value={code}
            onChange={(e) => setCode(sanitizeCode(e.target.value))}
            placeholder="latin_letters_and_digits"
            className="mt-1.5 h-10 w-full max-w-[420px] rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none placeholder:text-text-secondary focus:border-action-primary"
          />
        </div>

        <div className="mx-auto mt-6 max-w-[900px] rounded-2xl border border-border-default bg-bg-surface p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-text-primary">Поля значений</h2>
              <p className="mt-1 text-xs text-text-secondary">
                У каждого значения справочника всегда есть id и название — здесь можно добавить свои поля
              </p>
            </div>
            <button
              type="button"
              onClick={addField}
              className="flex h-9 shrink-0 items-center rounded-[9px] bg-action-primary px-3 text-xs font-medium text-text-inverse hover:bg-action-primary-hover"
            >
              + Добавить поле
            </button>
          </div>

          <div className="mt-4 overflow-x-auto">
            <div className="grid min-w-[640px] grid-cols-[1fr_1fr_140px_70px_32px] items-center gap-2 border-b border-border-default pb-2 text-[10px] font-semibold uppercase text-text-secondary">
              <span>Код</span>
              <span>Имя</span>
              <span>Тип</span>
              <span>Обяз.</span>
              <span />
            </div>
            <div className="mt-1 flex flex-col">
              <div className="grid min-w-[640px] grid-cols-[1fr_1fr_140px_70px_32px] items-center gap-2 rounded-lg px-1 py-1.5 opacity-60">
                <span className="truncate text-xs text-text-secondary">id</span>
                <span className="truncate text-xs text-text-secondary">Идентификатор</span>
                <span className="text-xs text-text-secondary">Системное</span>
                <span />
                <span />
              </div>
              <div className="grid min-w-[640px] grid-cols-[1fr_1fr_140px_70px_32px] items-center gap-2 rounded-lg px-1 py-1.5 opacity-60">
                <span className="truncate text-xs text-text-secondary">name</span>
                <span className="truncate text-xs text-text-secondary">Название</span>
                <span className="text-xs text-text-secondary">Строка</span>
                <span />
                <span />
              </div>
              {fields.length === 0 ? null : fields.map((field, i) => (
                  <div
                    key={i}
                    className="grid min-w-[640px] grid-cols-[1fr_1fr_140px_70px_32px] items-center gap-2 rounded-lg px-1 py-1.5 hover:bg-bg-surface-secondary"
                  >
                    <input
                      value={field.code}
                      onChange={(e) => {
                        const c = sanitizeCode(e.target.value);
                        setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, code: c, codeTouched: true } : f)));
                      }}
                      placeholder="code"
                      className="h-9 rounded-lg border border-border-default bg-bg-surface px-2 text-xs text-text-primary outline-none placeholder:text-text-secondary"
                    />
                    <input
                      value={field.name}
                      onChange={(e) => {
                        const n = e.target.value;
                        setFields((prev) =>
                          prev.map((f, idx) =>
                            idx === i ? { ...f, name: n, code: f.codeTouched ? f.code : slugify(n, `field_${i + 1}`) } : f,
                          ),
                        );
                      }}
                      placeholder="Название"
                      className="h-9 rounded-lg border border-border-default bg-bg-surface px-2 text-xs text-text-primary outline-none placeholder:text-text-secondary"
                    />
                    <select
                      value={field.data_type}
                      onChange={(e) =>
                        setFields((prev) =>
                          prev.map((f, idx) => (idx === i ? { ...f, data_type: e.target.value as DictionaryFieldDataType } : f)),
                        )
                      }
                      className="h-9 rounded-lg border border-border-default bg-bg-surface px-2 text-xs text-text-primary outline-none"
                    >
                      {(Object.entries(dataTypeLabels) as [DictionaryFieldDataType, string][]).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) =>
                        setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, required: e.target.checked } : f)))
                      }
                      className="mx-auto size-[18px] rounded-[5px] accent-action-primary"
                    />
                    <button
                      type="button"
                      onClick={() => removeField(i)}
                      title="Удалить поле"
                      className="mx-auto flex size-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-surface-secondary hover:text-status-danger"
                    >
                      ×
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {createError && <p className="mx-auto mt-4 max-w-[900px] text-xs font-medium text-status-danger">{createError}</p>}
      </div>

      <footer className="flex h-[70px] shrink-0 items-center gap-4 border-t border-border-default bg-bg-surface px-8">
        <Link
          to="/app/dictionaries"
          className="flex h-10 items-center rounded-[10px] border border-border-default bg-bg-surface px-4 text-[13px] font-medium text-text-primary hover:bg-bg-surface-secondary"
        >
          Отмена
        </Link>
        <button
          type="button"
          onClick={handleCreate}
          disabled={!name.trim() || !code.trim() || creating}
          className="ml-auto flex h-10 items-center rounded-[10px] bg-action-primary px-5 text-[13px] font-medium text-text-inverse transition-colors hover:bg-action-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {creating ? 'Создаём…' : 'Создать справочник'}
        </button>
      </footer>
    </div>
  );
}
